import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerDeployVault: build.mutation<
      ApiControllerDeployVaultApiResponse,
      ApiControllerDeployVaultApiArg
    >({
      query: (queryArg) => ({
        url: `/web/deploy/${queryArg.vaultId}`,
        method: 'POST',
      }),
    }),
    apiControllerWithdrawGas: build.mutation<
      ApiControllerWithdrawGasApiResponse,
      ApiControllerWithdrawGasApiArg
    >({
      query: (queryArg) => ({
        url: `/web/withdraw_gas`,
        method: 'POST',
        body: queryArg.withdrawGasRequest,
      }),
    }),
    apiControllerGetDeployer: build.query<
      ApiControllerGetDeployerApiResponse,
      ApiControllerGetDeployerApiArg
    >({
      query: (queryArg) => ({
        url: `/web/deployer/${queryArg.blockchainId}/${queryArg.deployerId}`,
      }),
    }),
    apiControllerFindDeployer: build.query<
      ApiControllerFindDeployerApiResponse,
      ApiControllerFindDeployerApiArg
    >({
      query: (queryArg) => ({
        url: `/web/find_deployer/${queryArg.blockchainId}/${queryArg.address}`,
      }),
    }),
    apiControllerAddDeployer: build.mutation<
      ApiControllerAddDeployerApiResponse,
      ApiControllerAddDeployerApiArg
    >({
      query: (queryArg) => ({
        url: `/web/add_deployer/${queryArg.blockchainId}`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerDeployVaultApiResponse =
  /** status 200 Deployment result */ AnyObjectResponse;
export type ApiControllerDeployVaultApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerWithdrawGasApiResponse =
  /** status 200 Withdrawal transaction sent */ WithdrawGasResponse;
export type ApiControllerWithdrawGasApiArg = {
  withdrawGasRequest: WithdrawGasRequest;
};
export type ApiControllerGetDeployerApiResponse =
  /** status 200 Returns deployer address and balance per blockchain */ DeployerResponse;
export type ApiControllerGetDeployerApiArg = {
  /** Blockchain ID */
  blockchainId: number;
  /** Deployer ID */
  deployerId: number;
};
export type ApiControllerFindDeployerApiResponse =
  /** status 200 Returns deployer information if found */ FindDeployerResponse;
export type ApiControllerFindDeployerApiArg = {
  /** Blockchain ID */
  blockchainId: number;
  /** Deployer address */
  address: string;
};
export type ApiControllerAddDeployerApiResponse =
  /** status 200 Deployer created */ AddDeployerResponse;
export type ApiControllerAddDeployerApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type AnyObjectResponse = {
  /** Operation status */
  status: boolean;
  /** Arbitrary response payload */
  data: {
    [key: string]: any;
  };
};
export type WithdrawGasData = {
  /** Transaction hash */
  txHash?: string;
  /** Drained native amount (human readable) */
  amount?: number;
  /** Source address */
  from?: string;
  /** Destination address */
  to?: string;
  /** Gas used by the transaction */
  gasUsed?: number;
  /** Gas spent in native currency */
  gasSpent?: number;
  /** Native gas currency symbol */
  gasCurrency?: string;
  /** Executor ID (returned only on executor→deployer flow) */
  executorId?: number;
  /** Deployer ID (returned on both flows: source deployer for deployer→target, vault's deployer for executor→deployer) */
  deployerId?: number;
  /** Blockchain ID on which the withdrawal was executed (use together with deployerId to invalidate per-chain deployer cache) */
  blockchainId?: number;
};
export type WithdrawGasResponse = {
  /** Operation status */
  status: boolean;
  data: WithdrawGasData;
};
export type WithdrawGasRequest = {
  /** Executor ID (executor → deployer flow). Mutually exclusive with deployerId. */
  executorId?: number;
  /** Deployer ID (deployer → target address flow). Requires `address` and `blockchainId`. */
  deployerId?: number;
  /** Target address (required when using deployerId) */
  address?: string;
  /** Blockchain ID (REQUIRED when using deployerId — a deployer can be linked to multiple chains) */
  blockchainId?: number;
};
export type DeployerData = {
  /** Deployer on-chain address */
  address?: string;
  /** Native balance on the requested blockchain (human readable) */
  balance?: number;
};
export type DeployerResponse = {
  /** Operation status */
  status: boolean;
  data: DeployerData;
};
export type FindDeployerData = {
  /** Deployer ID */
  id?: number;
};
export type FindDeployerResponse = {
  /** Operation status */
  status: boolean;
  data: FindDeployerData;
};
export type AddDeployerData = {
  /** Deployer ID */
  id: number;
  /** Generated deployer address */
  address: string;
};
export type AddDeployerResponse = {
  /** Operation status */
  status: boolean;
  data: AddDeployerData;
};
